'use client';

import { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Icon from '@/components/ui/AppIcon';

// Ice rink SVG heatmap data
const SHOT_ZONES = [
  { id: 'slot', label: 'Enclave', x: 47, y: 42, shots: 145, goals: 28, xG: 0.19 },
  { id: 'high_slot', label: 'Haut Enclave', x: 47, y: 30, shots: 89, goals: 8, xG: 0.09 },
  { id: 'left_circle', label: 'Cercle G', x: 30, y: 42, shots: 72, goals: 9, xG: 0.12 },
  { id: 'right_circle', label: 'Cercle D', x: 64, y: 42, shots: 68, goals: 10, xG: 0.15 },
  { id: 'left_point', label: 'Point G', x: 20, y: 20, shots: 55, goals: 3, xG: 0.05 },
  { id: 'right_point', label: 'Point D', x: 74, y: 20, shots: 52, goals: 4, xG: 0.08 },
  { id: 'behind_net', label: 'Derrière Filet', x: 47, y: 58, shots: 12, goals: 1, xG: 0.08 },
];

const MOMENTUM_DATA = [
  { time: '0:00', home: 50, away: 50 }, { time: '5:00', home: 55, away: 45 },
  { time: '10:00', home: 52, away: 48 }, { time: '15:00', home: 48, away: 52 },
  { time: '20:00', home: 58, away: 42 }, { time: '25:00', home: 62, away: 38 },
  { time: '30:00', home: 55, away: 45 }, { time: '35:00', home: 50, away: 50 },
  { time: '40:00', home: 45, away: 55 }, { time: '45:00', home: 42, away: 58 },
  { time: '50:00', home: 48, away: 52 }, { time: '55:00', home: 52, away: 48 },
  { time: '60:00', home: 56, away: 44 },
];

const WIN_PROB_DATA = [
  { time: '0:00', home: 52, away: 48 }, { time: '5:00', home: 55, away: 45 },
  { time: '10:00', home: 58, away: 42 }, { time: '15:00', home: 54, away: 46 },
  { time: '20:00', home: 62, away: 38 }, { time: '25:00', home: 68, away: 32 },
  { time: '30:00', home: 65, away: 35 }, { time: '35:00', home: 60, away: 40 },
  { time: '40:00', home: 55, away: 45 }, { time: '45:00', home: 48, away: 52 },
  { time: '50:00', home: 52, away: 48 }, { time: '55:00', home: 58, away: 42 },
  { time: '60:00', home: 63, away: 37 },
];

function getHeatColor(xG: number): string {
  if (xG >= 0.18) return '#ef4444';
  if (xG >= 0.12) return '#f97316';
  if (xG >= 0.08) return '#f59e0b';
  if (xG >= 0.05) return '#22c55e';
  return '#3b82f6';
}

function getCircleSize(shots: number): number {
  return Math.max(16, Math.min(40, shots / 5));
}

type ViewType = 'heatmap' | 'momentum' | 'winprob' | 'shots';

export default function VizEngineClient() {
  const [view, setView] = useState<ViewType>('heatmap');
  const [selectedZone, setSelectedZone] = useState<typeof SHOT_ZONES[0] | null>(null);

  const VIEWS: { id: ViewType; label: string; icon: string }[] = [
    { id: 'heatmap', label: 'Ice Heatmap', icon: 'FireIcon' },
    { id: 'momentum', label: 'Momentum', icon: 'ArrowTrendingUpIcon' },
    { id: 'winprob', label: 'Win Probability', icon: 'ChartBarIcon' },
    { id: 'shots', label: 'Shot Map', icon: 'BoltIcon' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center">
            <Icon name="PresentationChartBarIcon" size={20} className="text-purple-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Visualization Engine</h1>
            <p className="text-xs text-muted-foreground font-mono-data">Ice Heatmaps · Shot Maps · Momentum · Win Probability</p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-card border border-border rounded-xl p-1">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${view === v.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              <Icon name={v.icon as Parameters<typeof Icon>[0]['name']} size={12} />
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ice Heatmap */}
      {view === 'heatmap' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Carte Thermique Glace — BOS vs TOR</h3>
            {/* SVG Ice Rink */}
            <div className="relative w-full" style={{ paddingBottom: '60%' }}>
              <svg viewBox="0 0 94 57" className="absolute inset-0 w-full h-full" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0d1f3c 100%)' }}>
                {/* Rink outline */}
                <rect x="1" y="1" width="92" height="55" rx="8" fill="none" stroke="#1e3a5f" strokeWidth="1.5" />
                {/* Center line */}
                <line x1="47" y1="1" x2="47" y2="56" stroke="#1e3a5f" strokeWidth="0.5" strokeDasharray="2,2" />
                {/* Blue lines */}
                <line x1="31" y1="1" x2="31" y2="56" stroke="#1e4080" strokeWidth="0.8" />
                <line x1="63" y1="1" x2="63" y2="56" stroke="#1e4080" strokeWidth="0.8" />
                {/* Goal creases */}
                <rect x="43" y="48" width="8" height="6" rx="1" fill="none" stroke="#ef4444" strokeWidth="0.8" />
                <rect x="43" y="3" width="8" height="6" rx="1" fill="none" stroke="#22c55e" strokeWidth="0.8" />
                {/* Center circle */}
                <circle cx="47" cy="28.5" r="8" fill="none" stroke="#1e3a5f" strokeWidth="0.8" />
                <circle cx="47" cy="28.5" r="0.8" fill="#1e3a5f" />
                {/* Face-off circles */}
                <circle cx="20" cy="42" r="6" fill="none" stroke="#1e3a5f" strokeWidth="0.5" />
                <circle cx="74" cy="42" r="6" fill="none" stroke="#1e3a5f" strokeWidth="0.5" />
                <circle cx="20" cy="15" r="6" fill="none" stroke="#1e3a5f" strokeWidth="0.5" />
                <circle cx="74" cy="15" r="6" fill="none" stroke="#1e3a5f" strokeWidth="0.5" />

                {/* Shot zones */}
                {SHOT_ZONES.map(zone => (
                  <g key={zone.id} onClick={() => setSelectedZone(zone)} style={{ cursor: 'pointer' }}>
                    <circle
                      cx={zone.x} cy={zone.y}
                      r={getCircleSize(zone.shots) / 4}
                      fill={getHeatColor(zone.xG)}
                      opacity={selectedZone?.id === zone.id ? 0.9 : 0.65}
                      stroke={selectedZone?.id === zone.id ? '#fff' : 'none'}
                      strokeWidth="0.5"
                    />
                    <text x={zone.x} y={zone.y + 0.5} textAnchor="middle" dominantBaseline="middle"
                      fontSize="2.5" fill="white" fontWeight="bold">{zone.shots}</text>
                  </g>
                ))}
              </svg>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-3">
              {[
                { color: '#3b82f6', label: 'xG < 0.05' }, { color: '#22c55e', label: '0.05-0.08' },
                { color: '#f59e0b', label: '0.08-0.12' }, { color: '#f97316', label: '0.12-0.18' },
                { color: '#ef4444', label: 'xG > 0.18' },
              ].map((l, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }} />
                  <span className="text-2xs text-muted-foreground">{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Zones de Tir</h3>
            {SHOT_ZONES.map(zone => (
              <button key={zone.id} onClick={() => setSelectedZone(zone)}
                className={`w-full text-left p-3 rounded-xl border transition-all ${selectedZone?.id === zone.id ? 'bg-primary/10 border-primary/40' : 'bg-card border-border hover:border-primary/20'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{zone.label}</span>
                  <span className="text-xs font-mono-data" style={{ color: getHeatColor(zone.xG) }}>xG {zone.xG}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{zone.shots} tirs</span>
                  <span>{zone.goals} buts</span>
                  <span>{Math.round(zone.goals / zone.shots * 100)}% conv.</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Momentum Timeline */}
      {view === 'momentum' && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Momentum Timeline — BOS vs TOR</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={MOMENTUM_DATA}>
              <defs>
                <linearGradient id="homeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="awayGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#888' }} />
              <YAxis domain={[30, 70]} tick={{ fontSize: 10, fill: '#888' }} unit="%" />
              <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
              <ReferenceLine y={50} stroke="#444" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="home" name="BOS" stroke="#22c55e" fill="url(#homeGrad)" strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="away" name="TOR" stroke="#ef4444" fill="url(#awayGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-6 mt-2">
            <div className="flex items-center gap-2"><span className="w-3 h-0.5 bg-positive inline-block" /><span className="text-xs text-muted-foreground">BOS Momentum</span></div>
            <div className="flex items-center gap-2"><span className="w-3 h-0.5 bg-negative inline-block" /><span className="text-xs text-muted-foreground">TOR Momentum</span></div>
          </div>
        </div>
      )}

      {/* Win Probability */}
      {view === 'winprob' && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Win Probability Live — BOS vs TOR</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={WIN_PROB_DATA}>
              <defs>
                <linearGradient id="winHomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00D4FF" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00D4FF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#888' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#888' }} unit="%" />
              <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
              <ReferenceLine y={50} stroke="#444" strokeDasharray="4 4" label={{ value: '50%', fill: '#666', fontSize: 10 }} />
              <Area type="monotone" dataKey="home" name="BOS Win%" stroke="#00D4FF" fill="url(#winHomeGrad)" strokeWidth={2.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Shot Map */}
      {view === 'shots' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {['BOS', 'TOR'].map((team, ti) => (
            <div key={team} className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">{team} — Carte des Tirs</h3>
              <div className="relative w-full" style={{ paddingBottom: '60%' }}>
                <svg viewBox="0 0 94 57" className="absolute inset-0 w-full h-full" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #0d1f3c 100%)' }}>
                  <rect x="1" y="1" width="92" height="55" rx="8" fill="none" stroke="#1e3a5f" strokeWidth="1.5" />
                  <line x1="47" y1="1" x2="47" y2="56" stroke="#1e3a5f" strokeWidth="0.5" strokeDasharray="2,2" />
                  <rect x="43" y="48" width="8" height="6" rx="1" fill="none" stroke="#ef4444" strokeWidth="0.8" />
                  <circle cx="47" cy="28.5" r="8" fill="none" stroke="#1e3a5f" strokeWidth="0.8" />
                  {SHOT_ZONES.map((zone, i) => (
                    <g key={zone.id}>
                      {Array.from({ length: Math.min(zone.shots / 10, 8) }, (_, j) => (
                        <circle key={j}
                          cx={zone.x + (Math.sin(j * 1.2 + ti) * 6)}
                          cy={zone.y + (Math.cos(j * 1.2 + ti) * 4)}
                          r="1.2"
                          fill={j < zone.goals / 5 ? '#ef4444' : '#00D4FF'}
                          opacity={0.7}
                        />
                      ))}
                    </g>
                  ))}
                </svg>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary" /><span className="text-2xs text-muted-foreground">Tir</span></div>
                <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-negative" /><span className="text-2xs text-muted-foreground">But</span></div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
